import {
    DefaultKmlShapeColors,
    LineProperties,
    LineStroke,
    LineWidth,
    rulerFillColor,
    rulerOutlineColor,
} from "@nova/constants"
import { Point } from "@nova/data-create-polygon-kml"
import { Icon, Line, Ruler } from "@nova/protobuf"
import {
    decodeNumberToColor,
    decodeNumberToRGBA,
    encodeColorToNumber,
} from "@nova/serializer/src/client/util"
import { convertColourToKMLColour, escapeXmlEntities } from "@nova/util-strings"

export type Shape = {
    coordinates: {
        lng: number
        lat: number
    }[]
    properties: LineProperties
}

const createCoordinate = ([longitude, latitude]: Point) => {
    return `${longitude},${latitude},${0}`
}

const strokeProps: (thick: boolean) => Record<string, Record<string, string>> = (
    thick: boolean
) => ({
    [LineStroke.dotted]: {
        line_style: "dash",
        use_custom_dash: "1",
        customdash: thick ? "0.1;4.5" : "0.1;2.5",
        capstyle: "round",
        line_width: thick ? "2.25" : "1.25",
    },
    [LineStroke.squared]: {
        line_style: "dash",
        use_custom_dash: "1",
        customdash: thick ? "0.1;5" : "0.1;2",
        line_width: thick ? "2.25" : "1.25",
    },
    [LineStroke.normal]: {
        line_width: thick ? "1.5" : "0.75",
    },
    [LineStroke.dashed]: {
        line_style: "dash",
        use_custom_dash: "1",
        customdash: thick ? "5;9" : "2;3.5",
        line_width: thick ? "1.5" : "0.75",
    },
})

export const createRulerKml = (rulers: Ruler[], name = "rulers") =>
    createLineKml(
        rulers.map(
            (ruler) =>
                ({
                    ...ruler,
                    holes: [],
                    color: encodeColorToNumber(rulerOutlineColor),
                }) satisfies Line
        ),
        name
    )

export function createLineKml(lines: Line[], name = "lines") {
    const escapedName = escapeXmlEntities(name)
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapedName}</name>
    ${lines
        .map((line, i) => {
            const props = {
                color: line.color ? decodeNumberToRGBA(line.color).join(",") : "112,131,240,255",
                ...strokeProps(line.width === LineWidth.thick)[line.stroke || "normal"],
            }
            return `<Style id="${line.id}">
      <LineStyle>
        <color>${line.color !== undefined ? convertColourToKMLColour(decodeNumberToColor(line.color)) : "FF7083F0"}</color>
        <width>${line.width === LineWidth.thick ? 5 : 2}</width>
      </LineStyle>
    </Style>
    <Placemark>
      <name>${escapeXmlEntities(line.name || `line ${i}`)}</name>
      <description>${JSON.stringify(props)}</description>
      <styleUrl>#${line.id}</styleUrl>
      <LineString>
        <coordinates>
${line.coordinates
    .map(({ longitude, latitude }) => `          ${createCoordinate([longitude, latitude])}`)
    .join("\n")}
        </coordinates>
      </LineString>
    </Placemark>`
        })
        .join("\n")}
  </Document>
</kml>`
}

// const getSafeKmlColor = (color?: string) => {
//     if (!color) color = convertRGBAArrayToKMLColour([112, 131, 240, 255])
//     else {
//         const arr = convertHexStringToRGBArray(color)
//         if (arr) {
//             if (arr.length === 3) arr.push(255)
//             color = convertRGBAArrayToKMLColour(arr as [number, number, number, number])
//         }
//         if (!color) color = convertRGBAArrayToKMLColour([112, 131, 240, 255])
//     }
//     return color
// }

const getStyleId = (i: number) => `shape_${i}`

export const createRulerPolygonKml = (rulers: Ruler[], name = "rulers") =>
    createPolygonKml(
        rulers.map(
            (ruler) =>
                ({
                    ...ruler,
                    holes: [],
                    outline: encodeColorToNumber(rulerOutlineColor),
                    fill: encodeColorToNumber(rulerFillColor),
                }) satisfies Line
        ),
        name
    )

export const createPolygonKml = (polygons: Line[], name = "polygons") => {
    // Duplicate first vertex and append to create complete shape path.
    const closedPolygons = JSON.parse(JSON.stringify(polygons)) as Line[]
    closedPolygons.forEach((polygon, i) => {
        const path = polygon.coordinates
        const firstPoint = path.at(0)
        const lastPoint = path.at(-1)

        if (
            firstPoint?.longitude !== lastPoint?.longitude ||
            firstPoint?.latitude !== lastPoint?.latitude
        )
            closedPolygons[i].coordinates.push(path[0])
    })

    const escapedName = escapeXmlEntities(name)
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapedName}</name>
    ${closedPolygons
        .map((polygon, i) => {
            const stroke =
                typeof polygon.outline === "number"
                    ? decodeNumberToColor(polygon.outline, { isKml: true })
                    : DefaultKmlShapeColors.stroke
            const fill =
                typeof polygon.fill === "number"
                    ? decodeNumberToColor(polygon.fill, { isKml: true })
                    : DefaultKmlShapeColors.fill
            return `<Style id="${getStyleId(i)}">
      <LineStyle>
        <color>${stroke}</color>
        <width>${(polygon.width || 1) * 2}</width>
      </LineStyle>
      <PolyStyle>
      ${fill ? `<color>${fill}</color>` : "<fill>0</fill>"}
      </PolyStyle>
    </Style>`
        })
        .join("\n")}
      ${closedPolygons
          .map((polygon, i) => {
              return `
          <Placemark>
          <name>${escapeXmlEntities(polygon.name || `shape ${i}`)}</name>
          <styleUrl>#${getStyleId(i)}</styleUrl>
          <Polygon>
        <tessellate>1</tessellate>
        <outerBoundaryIs>
            <LinearRing>
                <coordinates>
                    ${polygon.coordinates
                        .map(({ longitude, latitude }) => createCoordinate([longitude, latitude]))
                        .join("\n")}
                </coordinates>
            </LinearRing>
        </outerBoundaryIs>
    </Polygon>
    </Placemark>`
          })
          .join("\n")}
  </Document>
</kml>`
}

export function createPointsKML(icons: Icon[], name = "points") {
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
        <name>${escapeXmlEntities(name)}</name>
        ${icons
            .map(
                (icon, i) =>
                    `<Placemark id="placemark-${i}">
                        <name>${escapeXmlEntities(icon.name)}</name>
                <Point id="point-${i}">
                    <coordinates>${icon.longitude},${icon.latitude},0.0</coordinates>
                </Point>
            </Placemark>`
            )
            .join("\n")}
    </Document>
</kml>`
}
